import React, { useState, useEffect } from 'react';
import { Settings, Eye, EyeOff, Save, CheckCircle, AlertCircle, RefreshCw, ShieldCheck, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { User } from '../types';
import api from '../api';
import { getInterviewConduct, saveInterviewConduct, getOfferTemplate, saveOfferTemplate } from '../api';
import MailIntakeSettings from './MailIntakeSettings';

interface Props {
  currentUser: User;
}

interface AiSettings {
  AI_API_KEY: string;
  AI_API_KEY_set: boolean;
  AI_API_URL: string;
  AI_MODEL: string;
  VISION_API_KEY: string;
  VISION_API_KEY_set: boolean;
  VISION_API_URL: string;
  VISION_MODEL: string;
}

export default function SystemSettings({ currentUser }: Props) {
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [showTextKey, setShowTextKey] = useState(false);
  const [conductContent, setConductContent] = useState('');
  const [savingConduct, setSavingConduct] = useState(false);
  const [offerTpl, setOfferTpl] = useState('');
  const [offerPlaceholders, setOfferPlaceholders] = useState<{ key: string; label: string; sample: string }[]>([]);
  const [savingOfferTpl, setSavingOfferTpl] = useState(false);

  const [textKey, setTextKey] = useState('');
  const [textUrl, setTextUrl] = useState('');
  const [textModel, setTextModel] = useState('');

  const [visionKey, setVisionKey] = useState('');
  const [visionUrl, setVisionUrl] = useState('');
  const [visionModel, setVisionModel] = useState('');

  useEffect(() => {
    fetchSettings();
    getInterviewConduct().then(d => setConductContent(d.content || '')).catch(() => {});
    getOfferTemplate().then(d => { setOfferTpl(d.content || ''); setOfferPlaceholders(d.placeholders || []); }).catch(() => {});
  }, []);

  const previewOfferTpl = () => {
    const sample: Record<string, string> = {};
    offerPlaceholders.forEach(p => { sample[p.key] = p.sample; });
    const filled = offerTpl.replace(/{{\s*([\w]+)\s*}}/g, (_m, k) => sample[k] ?? '');
    const w = window.open('', '_blank');
    if (!w) { toast.error('浏览器拦截了新窗口，请允许弹出'); return; }
    w.document.open(); w.document.write(filled); w.document.close();
  };

  async function fetchSettings() {
    setLoading(true);
    try {
      const data = await api.get('/admin/settings').then(r => r.data);
      setSettings(data);
      setTextUrl(data.AI_API_URL || 'https://api.deepseek.com/chat/completions');
      setTextModel(data.AI_MODEL || 'deepseek-chat');
      setVisionUrl(data.VISION_API_URL || 'https://api.openai.com/v1/chat/completions');
      setVisionModel(data.VISION_MODEL || 'gpt-4o');
    } catch (e: any) {
      toast.error('加载设置失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const body: Record<string, string> = {
        AI_API_URL: textUrl,
        AI_MODEL: textModel,
        VISION_API_URL: visionUrl,
        VISION_MODEL: visionModel,
      };
      if (textKey.trim()) body.AI_API_KEY = textKey.trim();
      if (visionKey.trim()) body.VISION_API_KEY = visionKey.trim();

      await api.post('/admin/settings', body).then(r => r.data);
      toast.success('设置已保存，立即生效');
      setTextKey('');
      setVisionKey('');
      await fetchSettings();
    } catch (e: any) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (currentUser.role !== 'admin') {
    return <div className="p-8 text-gray-500">仅管理员可访问系统设置</div>;
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
          <Settings size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-gray-900">系统设置</h1>
          <p className="text-sm text-gray-500">管理 AI 接口等系统级配置</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-12 justify-center">
          <RefreshCw size={16} className="animate-spin" />
          <span>加载中...</span>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Text AI Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-800 text-sm">🧠 文本 AI（简历解析 / 智能标签 / 搜索）</h2>
                  <p className="text-xs text-gray-500 mt-0.5">用于"AI识别简历"、自动打标签、智能搜索等文本能力（推荐 DeepSeek）</p>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${settings?.AI_API_KEY_set ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {settings?.AI_API_KEY_set
                    ? <><CheckCircle size={12} /> 已配置</>
                    : <><AlertCircle size={12} /> 未配置</>}
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  API Key
                  {settings?.AI_API_KEY_set && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">当前: {settings.AI_API_KEY}</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showTextKey ? 'text' : 'password'}
                    value={textKey}
                    onChange={e => setTextKey(e.target.value)}
                    placeholder={settings?.AI_API_KEY_set ? '留空则不修改已有 Key' : '输入 API Key，如 sk-...'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTextKey(!showTextKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showTextKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* API URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">API 地址</label>
                <input
                  type="text"
                  value={textUrl}
                  onChange={e => setTextUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">
                  DeepSeek: <code className="bg-gray-100 px-1 rounded">https://api.deepseek.com/chat/completions</code>
                </p>
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">模型</label>
                <div className="flex gap-2">
                  {['deepseek-chat', 'deepseek-reasoner', 'gpt-4o-mini'].map(m => (
                    <button
                      key={m}
                      onClick={() => setTextModel(m)}
                      className={`px-3 py-1.5 rounded-lg text-xs border font-medium transition-colors ${
                        textModel === m
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                  <input
                    type="text"
                    value={!['deepseek-chat', 'deepseek-reasoner', 'gpt-4o-mini'].includes(textModel) ? textModel : ''}
                    onChange={e => setTextModel(e.target.value)}
                    placeholder="自定义模型名"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Vision AI Section */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-800 text-sm">📸 Vision AI（截图简历识别）</h2>
                  <p className="text-xs text-gray-500 mt-0.5">插件截图后调用视觉 AI 识别简历内容，需支持图片输入的模型（推荐 gpt-4o）</p>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${settings?.VISION_API_KEY_set ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                  {settings?.VISION_API_KEY_set
                    ? <><CheckCircle size={12} /> 已配置</>
                    : <><AlertCircle size={12} /> 未配置</>}
                </div>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  API Key
                  {settings?.VISION_API_KEY_set && (
                    <span className="ml-2 text-xs text-gray-400 font-normal">当前: {settings.VISION_API_KEY}</span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={visionKey}
                    onChange={e => setVisionKey(e.target.value)}
                    placeholder={settings?.VISION_API_KEY_set ? '留空则不修改已有 Key' : '输入 API Key，如 sk-...'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {/* API URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">API 地址</label>
                <input
                  type="text"
                  value={visionUrl}
                  onChange={e => setVisionUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">
                  OpenAI: <code className="bg-gray-100 px-1 rounded">https://api.openai.com/v1/chat/completions</code>
                </p>
              </div>

              {/* Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">模型</label>
                <div className="flex gap-2">
                  {['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'].map(m => (
                    <button
                      key={m}
                      onClick={() => setVisionModel(m)}
                      className={`px-3 py-1.5 rounded-lg text-xs border font-medium transition-colors ${
                        visionModel === m
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                  <input
                    type="text"
                    value={!['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'].includes(visionModel) ? visionModel : ''}
                    onChange={e => setVisionModel(e.target.value)}
                    placeholder="自定义模型名"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-primary-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {saving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
              {saving ? '保存中...' : '保存设置'}
            </button>
          </div>

          {/* Interview Code of Conduct */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <ShieldCheck size={15} className="text-amber-500" />
                <h2 className="font-semibold text-gray-800 text-sm">面试官行为规范</h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">非HR面试官每次登录后首次打开候选人详情时，将弹窗展示此规范（须阅读3秒后方可关闭）</p>
            </div>
            <div className="p-5">
              <textarea
                value={conductContent}
                onChange={e => setConductContent(e.target.value)}
                rows={12}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                placeholder="输入面试官行为规范内容..."
              />
              <div className="flex justify-end mt-3">
                <button
                  onClick={async () => {
                    setSavingConduct(true);
                    try {
                      await saveInterviewConduct(conductContent);
                      toast.success('规范内容已保存');
                    } catch (e: any) {
                      toast.error('保存失败: ' + e.message);
                    } finally { setSavingConduct(false); }
                  }}
                  disabled={savingConduct}
                  className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 transition-colors"
                >
                  {savingConduct ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  {savingConduct ? '保存中...' : '保存规范'}
                </button>
              </div>
            </div>
          </div>

          {/* Offer 录用通知书模版 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText size={15} className="text-violet-500" />
                <h2 className="font-semibold text-gray-800 text-sm">Offer 信推模版</h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">HR 在 Offer 决策页点「生成Offer附件」时，用候选人信息替换模版占位符，生成正式录用通知书并「打印 / 保存为 PDF」。支持 HTML 排版。</p>
            </div>
            <div className="p-5">
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-600 mb-1.5">可用占位符（点击插入到光标处不支持，请手动复制）：</div>
                <div className="flex flex-wrap gap-1.5">
                  {offerPlaceholders.map(p => (
                    <span key={p.key} title={`${p.label}（示例：${p.sample}）`}
                      className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 font-mono cursor-default">
                      {'{{'}{p.key}{'}}'}
                    </span>
                  ))}
                </div>
              </div>
              <textarea
                value={offerTpl}
                onChange={e => setOfferTpl(e.target.value)}
                rows={16}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y"
                placeholder="输入 Offer 录用通知书 HTML 模版..."
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={previewOfferTpl}
                  className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                >
                  <Eye size={14} /> 预览（示例数据）
                </button>
                <button
                  onClick={async () => {
                    if (!offerTpl.trim()) { toast.error('模版内容不能为空'); return; }
                    setSavingOfferTpl(true);
                    try {
                      await saveOfferTemplate(offerTpl);
                      toast.success('Offer 模版已保存');
                    } catch (e: any) {
                      toast.error('保存失败: ' + e.message);
                    } finally { setSavingOfferTpl(false); }
                  }}
                  disabled={savingOfferTpl}
                  className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-50 transition-colors"
                >
                  {savingOfferTpl ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  {savingOfferTpl ? '保存中...' : '保存模版'}
                </button>
              </div>
            </div>
          </div>

          {/* Email auto-intake & AI pre-screening */}
          <MailIntakeSettings currentUser={currentUser} />
        </div>
      )}
    </div>
    </div>
  );
}
